/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "AWS PrivateLink", description = "Securely connect your warehouse to Hightouch using AWS PrivateLink.", lastUpdatedDate = new Date(1715731200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "AWS PrivateLink",
        description: "Securely connect your warehouse to Hightouch using AWS PrivateLink.",
        lastUpdatedDate: "Wed May 15 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Snowflake",
          "depth": 0,
          "id": "snowflake"
        }, {
          "value": "Prerequisites",
          "depth": 1,
          "id": "prerequisites"
        }, {
          "value": "Getting started",
          "depth": 1,
          "id": "getting-started"
        }, {
          "value": "Learn more",
          "depth": 1,
          "id": "learn-more"
        }, {
          "value": "Databricks",
          "depth": 0,
          "id": "databricks"
        }, {
          "value": "Prerequisites",
          "depth": 1,
          "id": "prerequisites-1"
        }, {
          "value": "Getting started",
          "depth": 1,
          "id": "getting-started-1"
        }, {
          "value": "Learn more",
          "depth": 1,
          "id": "learn-more-1"
        }, {
          "value": "Redshift",
          "depth": 0,
          "id": "redshift"
        }, {
          "value": "Prerequisites",
          "depth": 1,
          "id": "prerequisites-2"
        }, {
          "value": "Getting started",
          "depth": 1,
          "id": "getting-started-2"
        }, {
          "value": "Learn more",
          "depth": 1,
          "id": "learn-more-2"
        }, {
          "value": "Other integrations",
          "depth": 0,
          "id": "other-integrations"
        }, {
          "value": "Prerequisites",
          "depth": 1,
          "id": "prerequisites-3"
        }, {
          "value": "Getting started",
          "depth": 1,
          "id": "getting-started-3"
        }, {
          "value": "Learn more",
          "depth": 1,
          "id": "learn-more-3"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      h3: "h3",
      ul: "ul",
      li: "li",
      code: "code"
    }, _provideComponents(), props.components), {BtOnly, PylonLink} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "AWS PrivateLink"
      }), "\n", _jsxs(_components.p, {
        children: ["Using ", _jsx(_components.a, {
          href: "https://aws.amazon.com/privatelink/",
          children: "AWS PrivateLink"
        }), ", you can securely connect your AWS-deployed services to Hightouch."]
      }), "\n", _jsxs(_components.p, {
        children: ["Network traffic between Hightouch and your services connected by AWS PrivateLink stays on the ", _jsx(_components.a, {
          href: "https://aws.amazon.com/about-aws/global-infrastructure/global_network/",
          children: "AWS backbone network"
        }), " and doesn't travel over the public internet. That means that you can restrict public access to your services, and your data is never exposed to the public internet."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports managed AWS PrivateLink integrations for ", _jsx(_components.a, {
          href: "../sources/snowflake",
          children: "Snowflake"
        }), ",  ", _jsx(_components.a, {
          href: "../sources/amazon-redshift",
          children: "Redshift"
        }), ", and ", _jsx(_components.a, {
          href: "../sources/databricks",
          children: "Databricks"
        }), ", and other integrations with ", _jsx(_components.a, {
          href: "https://docs.aws.amazon.com/vpc/latest/privatelink/privatelink-share-your-services.html",
          children: "VPC Endpoint Service"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "snowflake",
        children: "Snowflake"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports connecting to your Snowflake account via AWS PrivateLink."
      }), "\n", _jsx(_components.h3, {
        id: "prerequisites",
        children: "Prerequisites"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Hightouch supports AWS PrivateLink on ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier"
          }), " accounts."]
        }), "\n", _jsxs(_components.li, {
          children: ["Snowflake supports AWS PrivateLink on ", _jsx(_components.a, {
            href: "https://docs.snowflake.com/en/user-guide/intro-editions.html#business-critical-edition",
            children: "Business Critical"
          }), " accounts or higher."]
        }), "\n", _jsxs(_components.li, {
          children: ["Your Snowflake account must be located in one of the following AWS regions: ", _jsx(_components.code, {
            children: "us-east-1"
          }), ", ", _jsx(_components.code, {
            children: "us-west-2"
          }), ", ", _jsx(_components.code, {
            children: "eu-west-1"
          }), ", ", _jsx(_components.code, {
            children: "ap-south-1"
          }), ", or ", _jsx(_components.code, {
            children: "ap-southeast-2"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Your Hightouch workspace must be located in the AWS ", _jsx(_components.code, {
            children: "us-east-1"
          }), ", ", _jsx(_components.code, {
            children: "eu-west-1"
          }), ", or ", _jsx(_components.code, {
            children: "ap-south-1"
          }), " region."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsxs(_components.p, {
        children: ["Please ", _jsx(PylonLink, {
          pylonText: "Hi, I'm interested in using AWS PrivateLink to connect Hightouch to my Snowflake account.",
          linkText: "reach out"
        }), " to our customer success team to get started with AWS PrivateLink for Snowflake."]
      }), "\n", _jsx(_components.h3, {
        id: "learn-more",
        children: "Learn more"
      }), "\n", _jsx(_components.p, {
        children: "Check out these AWS and Snowflake docs to learn more:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/vpc/latest/privatelink/what-is-privatelink.html",
            children: "What is AWS PrivateLink?"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://docs.snowflake.com/en/user-guide/admin-security-privatelink.html",
            children: "AWS PrivateLink & Snowflake"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "databricks",
        children: "Databricks"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can use AWS PrivateLink to connect to your Databricks workspace control plane. This allows Hightouch to securely interact with your Databricks workspace without exposing your data to the public internet."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch uses Databricks \"Front-end PrivateLink\", which is distinct from Databricks \"Back-end PrivateLink\", which is used to connect the Databricks control plane to a compute plane running in your AWS VPCs."
      }), "\n", _jsx(_components.h3, {
        id: "prerequisites-1",
        children: "Prerequisites"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Hightouch supports AWS PrivateLink on ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier"
          }), " accounts."]
        }), "\n", _jsxs(_components.li, {
          children: ["Your Hightouch workspace must be located in the AWS ", _jsx(_components.code, {
            children: "us-east-1"
          }), ", ", _jsx(_components.code, {
            children: "eu-west-1"
          }), ", or ", _jsx(_components.code, {
            children: "ap-south-1"
          }), " region."]
        }), "\n", _jsxs(_components.li, {
          children: ["Your Databricks workspace must be located in one of the following AWS regions: ", _jsx(_components.code, {
            children: "us-east-1"
          }), ", ", _jsx(_components.code, {
            children: "us-west-2"
          }), ", ", _jsx(_components.code, {
            children: "eu-west-1"
          }), ", ", _jsx(_components.code, {
            children: "ap-south-1"
          }), ", or ", _jsx(_components.code, {
            children: "ap-southeast-2"
          })]
        }), "\n", _jsx(_components.li, {
          children: "Your Databricks workspace must have been created with a \"Private Access Settings\" object. Databricks does not support setting a Private Access Settings object on existing workspaces. If you need to use PrivateLink with an existing workspace, you will need to create a new workspace with the appropriate settings."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "getting-started-1",
        children: "Getting started"
      }), "\n", _jsxs(_components.p, {
        children: ["Please ", _jsx(PylonLink, {
          pylonText: "Hi, I'm interested in using AWS PrivateLink to connect Hightouch to my Databricks workspace.",
          linkText: "reach out"
        }), " to our customer success team to get started with AWS PrivateLink for Databricks."]
      }), "\n", _jsx(_components.h3, {
        id: "learn-more-1",
        children: "Learn more"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://docs.databricks.com/en/security/network/classic/privatelink.html",
            children: "Enable AWS PrivateLink"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "redshift",
        children: "Redshift"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports connecting to your Redshift cluster via PrivateLink with ", _jsx(_components.a, {
          href: "https://docs.aws.amazon.com/redshift/latest/mgmt/managing-cluster-cross-vpc.html",
          children: "Redshift-managed VPC Endpoints"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "prerequisites-2",
        children: "Prerequisites"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Hightouch supports AWS PrivateLink on ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier"
          }), " accounts."]
        }), "\n", _jsxs(_components.li, {
          children: ["Your Redshift cluster must be located in one of the following AWS regions: ", _jsx(_components.code, {
            children: "us-east-1"
          }), ", ", _jsx(_components.code, {
            children: "us-west-2"
          }), ", ", _jsx(_components.code, {
            children: "eu-west-1"
          }), ", ", _jsx(_components.code, {
            children: "ap-south-1"
          }), ", or ", _jsx(_components.code, {
            children: "ap-southeast-2"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Your Hightouch workspace must be located in the AWS ", _jsx(_components.code, {
            children: "us-east-1"
          }), ", ", _jsx(_components.code, {
            children: "eu-west-1"
          }), ", or ", _jsx(_components.code, {
            children: "ap-south-1"
          }), " region."]
        }), "\n", _jsxs(_components.li, {
          children: ["Your Redshift cluster must be of the ", _jsx(_components.code, {
            children: "RA3"
          }), " node type"]
        }), "\n", _jsxs(_components.li, {
          children: ["Your Redshift cluster must have ", _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/redshift/latest/mgmt/managing-cluster-recovery.html",
            children: "Cluster Relocation"
          }), " enabled"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "getting-started-2",
        children: "Getting started"
      }), "\n", _jsxs(_components.p, {
        children: ["Please ", _jsx(PylonLink, {
          pylonText: "Hi, I'm interested in using AWS PrivateLink to connect Hightouch to my Redshift cluster.",
          linkText: "reach out"
        }), " to our customer success team to get started with AWS PrivateLink for Redshift."]
      }), "\n", _jsx(_components.h3, {
        id: "learn-more-2",
        children: "Learn more"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/vpc/latest/privatelink/what-is-privatelink.html",
            children: "What is AWS PrivateLink?"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/redshift/latest/mgmt/managing-cluster-cross-vpc.html",
            children: "Redshift-managed VPC endpoint documentation"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "other-integrations",
        children: "Other integrations"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch can connect to any source or destination exposed via a ", _jsx(_components.a, {
          href: "https://docs.aws.amazon.com/vpc/latest/privatelink/privatelink-share-your-services.html",
          children: "VPC Endpoint Service"
        }), ". This allows you to expose any service running in your VPC to Hightouch without exposing it to the public internet."]
      }), "\n", _jsx(_components.h3, {
        id: "prerequisites-3",
        children: "Prerequisites"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Hightouch supports AWS PrivateLink on ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier"
          }), " accounts."]
        }), "\n", _jsxs(_components.li, {
          children: ["Your integration (source / destination) must be exposed via a ", _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/vpc/latest/privatelink/privatelink-share-your-services.html",
            children: "VPC Endpoint Service"
          }), ". This generally requires a ", _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/elasticloadbalancing/latest/network/introduction.html",
            children: "Network Load Balancer"
          }), " to sit in front of your service."]
        }), "\n", _jsxs(_components.li, {
          children: ["Your VPC Endpoint Service must be located in one of the following AWS regions: ", _jsx(_components.code, {
            children: "us-east-1"
          }), ", ", _jsx(_components.code, {
            children: "us-west-2"
          }), ", ", _jsx(_components.code, {
            children: "eu-west-1"
          }), ", ", _jsx(_components.code, {
            children: "ap-south-1"
          }), ", or ", _jsx(_components.code, {
            children: "ap-southeast-2"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Your Hightouch workspace must be located in the AWS ", _jsx(_components.code, {
            children: "us-east-1"
          }), ", ", _jsx(_components.code, {
            children: "eu-west-1"
          }), ", or ", _jsx(_components.code, {
            children: "ap-south-1"
          }), " region."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "getting-started-3",
        children: "Getting started"
      }), "\n", _jsxs(_components.p, {
        children: ["Please ", _jsx(PylonLink, {
          pylonText: "Hi, I'm interested in using AWS PrivateLink to connect Hightouch to my VPC Endpoint Service.",
          linkText: "reach out"
        }), " to our customer success team to get started with AWS PrivateLink for VPC Endpoint Services."]
      }), "\n", _jsx(_components.h3, {
        id: "learn-more-3",
        children: "Learn more"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/vpc/latest/privatelink/what-is-privatelink.html",
            children: "What is AWS PrivateLink?"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/elasticloadbalancing/latest/network/introduction.html",
            children: "What is a Network Load Balancer?"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://docs.aws.amazon.com/vpc/latest/privatelink/privatelink-share-your-services.html",
            children: "Share your services through AWS PrivateLink"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
